// ---------------------------------------------------------------------------
// Provider Types
// ---------------------------------------------------------------------------

export type ProviderConfigStatus = 'active' | 'disabled' | 'error' | 'pending_setup';

export type ProviderType =
  | 'oneroster_1.2'
  | 'csv_import'
  | 'clever'
  | 'classlink'
  | 'powerschool_plugin'
  | 'aeries'
  | 'edfi'
  | 'facts_sis';

export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  'oneroster_1.2': 'OneRoster 1.2',
  csv_import: 'CSV Import',
  clever: 'Clever',
  classlink: 'ClassLink',
  powerschool_plugin: 'PowerSchool Plugin',
  aeries: 'Aeries',
  edfi: 'Ed-Fi',
  facts_sis: 'FACTS SIS (RenWeb)',
};

// ---------------------------------------------------------------------------
// Read types (API responses)
// ---------------------------------------------------------------------------

export interface RosterProvider {
  id: number;
  public_id: string;
  provider_type: ProviderType;
  display_name: string;
  district: number | null;
  base_url: string;
  status: ProviderConfigStatus;
  auth_config_summary: Record<string, boolean>;
  sync_config: SyncConfig;
  field_mappings: Record<string, string>;
  last_full_sync: string | null;
  last_delta_sync: string | null;
  last_error: string | null;
}

export interface SyncConfig {
  delta_sync_minutes?: number;
  full_sync_hours?: number;
  /**
   * FACTS SIS only: the configSchoolId that scopes a
   * district-wide Facts-Api-Key to one school (x-configSchoolId header).
   * Omit for school-specific keys.
   */
  config_school_id?: string;
}

// ---------------------------------------------------------------------------
// Write types (form payloads)
// ---------------------------------------------------------------------------

export interface RosterProviderWrite {
  provider_type: ProviderType;
  display_name: string;
  district: number | null;
  base_url: string;
  auth_config?: Record<string, string>;
  sync_config?: SyncConfig;
  status?: ProviderConfigStatus;
}

// ---------------------------------------------------------------------------
// Auth config field definitions per provider type
// ---------------------------------------------------------------------------

export interface AuthField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'textarea';
}

export const AUTH_FIELDS_BY_TYPE: Record<ProviderType, AuthField[]> = {
  'oneroster_1.2': [
    { key: 'client_id', label: 'Client ID', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', type: 'password' },
    { key: 'token_url', label: 'Token URL', type: 'text' },
  ],
  csv_import: [
    { key: 'sftp_host', label: 'SFTP Host', type: 'text' },
    { key: 'sftp_username', label: 'SFTP Username', type: 'text' },
    { key: 'sftp_password', label: 'SFTP Password', type: 'password' },
    { key: 'sftp_private_key', label: 'SFTP Private Key', type: 'textarea' },
  ],
  clever: [{ key: 'bearer_token', label: 'Bearer Token', type: 'password' }],
  classlink: [
    { key: 'client_id', label: 'Client ID', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', type: 'password' },
  ],
  powerschool_plugin: [
    { key: 'client_id', label: 'Client ID', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', type: 'password' },
    { key: 'plugin_token', label: 'Plugin Token', type: 'password' },
  ],
  aeries: [
    { key: 'api_key', label: 'API Key', type: 'password' },
    { key: 'certificate', label: 'Certificate', type: 'textarea' },
  ],
  edfi: [
    { key: 'client_id', label: 'Client ID', type: 'text' },
    { key: 'client_secret', label: 'Client Secret', type: 'password' },
    { key: 'api_version', label: 'API Version', type: 'text' },
  ],
  // Two-header model — both keys sent on every request. No key
  // exchange exists; the school key is generated by the school's FACTS
  // admin (API Configuration → API Keys).
  facts_sis: [
    { key: 'subscription_key', label: 'Partner Subscription Key', type: 'password' },
    { key: 'api_key', label: 'School API Key (Facts-Api-Key)', type: 'password' },
  ],
};

// ---------------------------------------------------------------------------
// Connection test
// ---------------------------------------------------------------------------

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  endpoints_tested: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export type SyncMode = 'full' | 'delta' | 'emergency';

export interface SyncTriggerRequest {
  mode: SyncMode;
  school_source_id?: string;
}

export interface SyncTriggerResponse {
  task_id: string;
  mode: SyncMode;
  provider_config_id: number;
  message: string;
}

export interface SyncHistoryEntry {
  id: number;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  counts: {
    schools_synced?: number;
    persons_created?: number;
    persons_updated?: number;
    persons_deactivated?: number;
    classes_synced?: number;
    enrollments_synced?: number;
  };
  error_count: number;
  warning_count: number;
}

export interface SyncStatusResponse {
  provider: {
    id: number;
    display_name: string;
    provider_type: string;
    status: ProviderConfigStatus;
    last_full_sync: string | null;
    last_delta_sync: string | null;
    last_error: string | null;
  };
  recent_syncs: SyncHistoryEntry[];
}

// ---------------------------------------------------------------------------
// CSV Upload
// ---------------------------------------------------------------------------

export interface CsvUploadResult {
  status: string;
  persons_created: number;
  persons_updated: number;
  persons_deactivated: number;
  schools_synced: number;
  enrollments_synced: number;
  errors: string[];
  warnings: string[];
}
